import {
	type EnvironmentName,
	type DeploymentEnvironment as Environment,
	type DeploymentEnvironmentType as EnvironmentType,
	DeploymentEnvironmentTypeOptions,
	type EnvironmentTypeConcatName,
} from '../../../../common/types';

/**
 * A checkbox entry ID can represent either an environment *name* or an environment *type*.
 *
 * These utils allow these two cases to be disambiguated without the possibility of naming
 * clashes. We need to be able to tell apart an environment name called 'PRODUCTION' from
 * the 'PRODUCTION' environment type.
 */

export type EntryId = string;

export const entryIdFromEnvironmentType = (type: EnvironmentType): EntryId =>
	`environment-type:${type}`;

export const entryIdFromEnvironmentName = (name: EnvironmentName): EntryId =>
	`environment-name:${name}`;

export const entryIdFromEnvironmentTypeConcatName = (name: EnvironmentTypeConcatName): EntryId =>
	`environment-name:${name}`;

export const entryIdFromEnvironmentTypeAndName = (
	type: EnvironmentType,
	name: EnvironmentName,
): EntryId => `environment-name:${composeEnvironmentKey(type, name)}`;

export const entryIdFromEnvironment = (environemnt: Environment): EntryId =>
	entryIdFromEnvironmentTypeAndName(environemnt.type, environemnt.displayName);

export const entryIdFromRawString = (value: string): EntryId => {
	if (value.startsWith('environment-type:') || value.startsWith('environment-name:')) {
		return value;
	}
	throw new Error('Invalid entry ID');
};

export const entryIdToRawString = (entryId: EntryId): string => entryId;

export const composeEnvironmentKey = (type: EnvironmentType, name: string): string =>
	`${type}-${name}`;

export const extractEnvNameFromEnvironmentKey = (key: EnvironmentTypeConcatName): string =>
	key.slice(key.indexOf('-') + 1);

type CaseHandlers<T> = {
	environmentType: (arg1: EnvironmentType) => T;
	environmentName: (arg1: EnvironmentName | EnvironmentTypeConcatName) => T;
};

/**
 * Allows the caller to safely extract the underlying environment type or name from an entry ID.
 */
export const matchEntryId = <T,>(entryId: EntryId, handlers: CaseHandlers<T>): T => {
	const separatorIndex = entryId.indexOf(':');
	const entryKind = entryId.slice(0, separatorIndex);
	const entryValue = entryId.slice(separatorIndex + 1);

	if (entryKind === 'environment-type') {
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly PRODUCTION: "PRODUCTION"; readonly STAGING: "STAGING"; readonly TESTING: "TESTING"; readonly DEVELOPMENT: "DEVELOPMENT"; readonly UNMAPPED: "UNMAPPED"; }'.
		const environmentType = DeploymentEnvironmentTypeOptions[entryValue];
		return handlers.environmentType(environmentType);
	}
	if (entryKind === 'environment-name') {
		return handlers.environmentName(entryValue);
	}
	throw new Error('Invalid entry ID');
};
